import { useQuery } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { accessTokenAtom } from "../../recoil/auth";
import styled from "styled-components";
import media from "../../static/style/media";
import { userInfoAtom } from "../../recoil/user";
import { getDevices } from "../../apis/api/Device/getDevices";
import { getCategory } from "../../apis/api/Category/getCategory";
import { useTranslation } from "react-i18next";
import { useTimeFormat } from "../../library/useTimeFormat";

const tableTitle = ["name", "serial number", "type", "creation date", "update date"];

function DeviceList() {
  const accessToken = useRecoilValue(accessTokenAtom);
  const userInfo = useRecoilValue(userInfoAtom);
  const { data: deviceData } = useQuery({
    queryKey: ["devices"],
    queryFn: () => getDevices(accessToken),
  });

  const { data: categoryData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => getCategory(accessToken),
  });

  const { t } = useTranslation();
  const { date } = useTimeFormat();

  return (
    <Container>
      <Title>
        <span>{userInfo?.teamName}</span> {t(`device list`)}
      </Title>
      {categoryData?.map((category, index) => {
        const filteredDevices = deviceData?.filter(
          (device) => device.categoryName === category.categoryName
        );

        return (
          <CategorySection key={index}>
            <ListTitle>{category?.categoryName}</ListTitle>
            {filteredDevices && filteredDevices.length > 0 ? (
              <Table>
                <thead>
                  <TableHeaderRow>
                    {tableTitle?.map((item) => (
                      <TableHeader key={item}>{t(item)}</TableHeader>
                    ))}
                  </TableHeaderRow>
                </thead>
                <tbody>
                  {filteredDevices.map((device) => (
                    <TableRow key={device.serial}>
                      <TableCell>{device.name}</TableCell>
                      <TableCell>{device.serial}</TableCell>
                      <TableCell>{device.type}</TableCell>
                      <TableCell>{date(device.createdAt)}</TableCell>
                      <TableCell>{date(device.updatedAt)}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            ) : (
              <NoDeviceText>{t(`no device`)}</NoDeviceText>
            )}
          </CategorySection>
        );
      })}
    </Container>
  );
}

// Styled components
const Container = styled.div`
  padding: 20px 30px;
  margin: 0 auto 0 30px;
  width: calc(100% - 30px);
  box-sizing: border-box;
  overflow: auto;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  ${media.small`
    padding: 15px;
    width: 100%;
  `};
`;

const Title = styled.div`
  height: 60px;
  font-weight: 700;
  font-size: 24px;
  line-height: 60px;
  background-color: #f0f0f0;
  padding-left: 20px;
  margin-bottom: 30px;
  border-radius: 8px;
  color: #333;

  span {
    color: #175eb4;
    font-weight: 800;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 30px;
`;

const ListTitle = styled.div`
  font-weight: 700;
  font-size: 20px;
  color: #175eb4;
  border-bottom: 2px solid #ddd;
  padding-bottom: 10px;
  margin-bottom: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
`;

const TableHeaderRow = styled.tr``;

const TableHeader = styled.th`
  background-color: #f0f0f0;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  text-align: left;

  ${media.small`
    font-size: 12px;
  `};
`;

const TableRow = styled.tr`
  transition: background-color 0.3s ease;
  &:nth-child(even) {
    background-color: #fafafa;
  }

  &:hover {
    background-color: #f0f8ff;
  }
`;

const TableCell = styled.td`
  padding: 10px 20px;
  font-size: 14px;
  color: #666;
  border-bottom: 1px solid #eee;
  text-align: left;

  ${media.small`
    font-size: 12px;
  `};
`;

const NoDeviceText = styled.p`
  font-size: 14px;
  color: #888;
  text-align: center;
`;

export default DeviceList;
